import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../context/ThemeContext';

// Cortex PortalDropdown (DESIGN_LANGUAGE_SPEC §27: "portal dropdowns — zero native
// <select>"). The menu is rendered through a portal to document.body and positioned under
// its trigger from the trigger's getBoundingClientRect. It closes on outside-click and
// Escape, and is keyboard navigable (Up/Down/Enter/Escape). No native <select> is used.
//
// Tab/Shift+Tab are trapped within the open menu (WCAG 2.1.2 / 2.4.3) so focus cannot escape to
// the page behind the portal (the menu's content — e.g. a search input — lives in a portal that
// is visually over, but DOM-detached from, the trigger). When the menu has no focusable content,
// Tab instead closes the menu and returns focus to the trigger.

// Tab order within the portal menu (e.g. the search input + enabled option rows).
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DropdownItem {
  /** Stable identifier for the option. */
  value: string;
  /** Rendered content for the row. */
  label: ReactNode;
  disabled?: boolean | undefined;
}

export interface TriggerRenderArgs {
  open: boolean;
  toggle: () => void;
  /** Callback ref to attach to the trigger button so positioning can read its rect. */
  ref: (node: HTMLButtonElement | null) => void;
  triggerProps: {
    'aria-haspopup': 'listbox';
    'aria-expanded': boolean;
    'aria-controls': string;
  };
}

export interface PortalDropdownProps {
  /** Render-prop for the trigger. Receives open state + a toggle handler. */
  trigger: (args: TriggerRenderArgs) => ReactNode;
  items: DropdownItem[];
  onSelect: (value: string) => void;
  /** Controlled-open override; if omitted the component manages its own open state. */
  isOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** Optional content rendered above the items (e.g. a search input). */
  header?: ReactNode | undefined;
  /** Optional content rendered when there are no items. */
  emptyContent?: ReactNode | undefined;
  /** Externally controlled active (highlighted) index, for autocomplete composition. */
  activeIndex?: number | undefined;
  onActiveIndexChange?: ((index: number) => void) | undefined;
  menuLabel?: string | undefined;
}

interface Position {
  top: number;
  left: number;
  width: number;
}

export function PortalDropdown({
  trigger,
  items,
  onSelect,
  isOpen,
  onOpenChange,
  header,
  emptyContent,
  activeIndex,
  onActiveIndexChange,
  menuLabel,
}: PortalDropdownProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const menuId = useId();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isOpen ?? uncontrolledOpen;

  const [internalActive, setInternalActive] = useState(0);
  const active = activeIndex ?? internalActive;

  const [position, setPosition] = useState<Position>({ top: 0, left: 0, width: 0 });

  const setOpen = useCallback(
    (next: boolean) => {
      if (isOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isOpen, onOpenChange],
  );

  const setActive = useCallback(
    (index: number) => {
      if (activeIndex === undefined) setInternalActive(index);
      onActiveIndexChange?.(index);
    },
    [activeIndex, onActiveIndexChange],
  );

  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  // Position the menu under the trigger using its bounding rect.
  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handle = () => updatePosition();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [open, updatePosition]);

  // Outside-click closes the menu.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, setOpen]);

  const enabledIndexes = items
    .map((item, i) => (item.disabled ? -1 : i))
    .filter((i) => i >= 0);

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (enabledIndexes.length === 0) return;
      const currentPos = enabledIndexes.indexOf(active);
      const nextPos =
        currentPos === -1
          ? direction === 1
            ? 0
            : enabledIndexes.length - 1
          : (currentPos + direction + enabledIndexes.length) %
            enabledIndexes.length;
      setActive(enabledIndexes[nextPos]);
    },
    [active, enabledIndexes, setActive],
  );

  // Trap Tab/Shift+Tab inside the open menu. With no focusable content, fall back to closing the
  // menu and returning focus to the trigger so focus never lands behind the portal.
  const handleTab = (e: React.KeyboardEvent) => {
    const root = menuRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Enter': {
        e.preventDefault();
        const item = items[active];
        if (item && !item.disabled) {
          onSelect(item.value);
          setOpen(false);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        handleTab(e);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {trigger({
        open,
        toggle,
        ref: (node) => {
          triggerRef.current = node;
        },
        triggerProps: {
          'aria-haspopup': 'listbox',
          'aria-expanded': open,
          'aria-controls': menuId,
        },
      })}

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="listbox"
            aria-label={menuLabel ?? 'Options'}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            style={{
              position: 'absolute',
              top: position.top,
              left: position.left,
              minWidth: position.width,
            }}
            className={`z-[99999] max-h-72 overflow-y-auto rounded-xl border p-1 shadow-xl outline-none ${
              isDark
                ? 'border-white/[0.1] bg-[#16161e] shadow-black/50'
                : 'border-slate-200 bg-white shadow-slate-200/60'
            }`}
          >
            {header}
            {items.length === 0
              ? emptyContent
              : items.map((item, i) => {
                  const isActive = i === active;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      disabled={item.disabled}
                      onMouseEnter={() => !item.disabled && setActive(i)}
                      onClick={() => {
                        if (item.disabled) return;
                        onSelect(item.value);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                        isActive
                          ? isDark
                            ? 'bg-indigo-500/20 text-white'
                            : 'bg-indigo-50 text-indigo-700'
                          : isDark
                            ? 'text-white/70 hover:bg-white/[0.06]'
                            : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
          </div>,
          document.body,
        )}
    </>
  );
}
